/**
 * Intelligent autofill for a new contract application.
 *
 * An agent contracting with a second carrier answers almost the same questions
 * they already answered for the first: same address, same licensed states, same
 * W9 details, same bank. This module turns their profile and their previous
 * submissions into per-field suggestions for the next application.
 *
 * The engine is deliberately pure and lives in `@aam/shared` so the backend can
 * build a plan from decrypted submissions and the frontend can render exactly
 * the same reasoning next to each field.
 *
 * Three ideas do the work:
 *
 *  - **Provenance.** Every suggestion says where it came from — the portal
 *    profile, or a specific previous contract with its carriers and date.
 *  - **Agreement and recency.** A value repeated across contracts is trusted;
 *    a value that changed between them is offered as the most recent answer
 *    with the older ones listed as alternatives, and flagged for review.
 *  - **Staleness.** Answers decay at different rates. An address from three
 *    years ago is a guess; a date of birth never goes stale. Fields past their
 *    window are still filled, but flagged so the agent confirms them.
 *
 * PII (SSN, DOB, bank numbers) is *never* carried silently: the plan marks
 * those fields sensitive, the backend masks their values, and the agent has to
 * ask for each one explicitly.
 */
import { CARRIER_DISPLAY_NAMES } from "./carriers.js";
import { CARRIER_VALUE_TO_CARRIER } from "./forms.js";
import { PII_ENCRYPTED_FIELD_IDS } from "./pii.js";
import { signatureStrokesKey } from "./signature.js";
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * Fields worth carrying forward, and how long each stays trustworthy.
 *
 * Anything absent from this map is never autofilled. That is the safe default:
 * a new field added to the form schema has to be opted in here deliberately,
 * rather than silently inheriting an answer written for a different carrier.
 */
export const AUTOFILL_FIELD_POLICY = {
    // Identity — corrected in the profile, rarely re-typed.
    fullName: { category: "identity", trustDays: 1095 },
    firstName: { category: "identity", trustDays: 1095 },
    lastName: { category: "identity", trustDays: 1095 },
    email: { category: "identity", trustDays: 730 },
    phone: { category: "identity", trustDays: 730 },
    // Licensing — the agent's professional identity.
    npn: { category: "licensing", trustDays: 1095 },
    ssn: { category: "licensing", trustDays: 3650, sensitive: true },
    dateOfBirth: { category: "licensing", trustDays: 3650, sensitive: true },
    statesToSell: { category: "licensing", trustDays: 365 },
    isPrincipal: { category: "licensing", trustDays: 545 },
    agencyEIN: { category: "licensing", trustDays: 1095 },
    agencyNPN: { category: "licensing", trustDays: 1095 },
    // Addresses — people move.
    address: { category: "address", trustDays: 365 },
    addressLine2: { category: "address", trustDays: 365 },
    city: { category: "address", trustDays: 365 },
    state: { category: "address", trustDays: 365 },
    zipCode: { category: "address", trustDays: 365 },
    sameAsBusinessAddress: { category: "address", trustDays: 365 },
    businessAddress: { category: "address", trustDays: 365 },
    businessAddressLine2: { category: "address", trustDays: 365 },
    businessCity: { category: "address", trustDays: 365 },
    businessState: { category: "address", trustDays: 365 },
    businessZipCode: { category: "address", trustDays: 365 },
    // W9 — mirrors the address and legal name above.
    w9Name: { category: "tax", trustDays: 545 },
    w9BusinessName: { category: "tax", trustDays: 545 },
    w9TaxClassification: { category: "tax", trustDays: 545 },
    w9LLCTaxClassification: { category: "tax", trustDays: 545 },
    w9Address: { category: "tax", trustDays: 365 },
    w9CityStateZip: { category: "tax", trustDays: 365 },
    // Banking — commission money moves here, so the window is the tightest.
    eftBankName: { category: "banking", trustDays: 270 },
    eftAccountType: { category: "banking", trustDays: 270 },
    eftRoutingNumber: { category: "banking", trustDays: 270, sensitive: true },
    eftAccountNumber: { category: "banking", trustDays: 270, sensitive: true },
    // Upline / manager contacts collected on PPG intake.
    managerName: { category: "contacts", trustDays: 545 },
    managerEmail: { category: "contacts", trustDays: 545 },
};
/**
 * Never carried forward, even though previous submissions contain them.
 *
 * Signatures and acknowledgments must be given again for each contract —
 * re-using a signature would forge consent for a document the agent has not
 * seen. Uploads point at files scoped to the submission that owns them, and
 * the rest are answers about *this* application, not about the agent.
 */
export const AUTOFILL_EXCLUDED_FIELD_IDS = new Set([
    "selectedCarriers",
    "formVariant",
    "agentEmail",
    "reviewSummary",
    "message",
    "trainingStartDate",
    "hasRelease",
    "releaseUpload",
    "w9Signature",
    signatureStrokesKey("w9Signature"),
    "w9SignatureAcknowledgment",
    "eftSignature",
    signatureStrokesKey("eftSignature"),
    "eftSignatureAcknowledgment",
    "commissionAcknowledgment",
]);
/** Profile fields that overrule anything found in submission history. */
const PROFILE_FIELD_IDS = ["email", "firstName", "lastName", "phone", "npn"];
export const AUTOFILL_SENSITIVE_FIELD_IDS = PII_ENCRYPTED_FIELD_IDS;
export function isSensitiveAutofillField(fieldId) {
    return AUTOFILL_SENSITIVE_FIELD_IDS.includes(fieldId);
}
function isBlank(value) {
    if (value === null || value === undefined)
        return true;
    if (typeof value === "string")
        return value.trim().length === 0;
    if (Array.isArray(value))
        return value.length === 0;
    if (typeof value === "object")
        return Object.keys(value).length === 0;
    return false;
}
function normalizeValue(value) {
    if (typeof value === "string")
        return value.trim();
    return value;
}
/** Identity for comparison only — order-insensitive for multi-selects. */
function comparisonKey(value) {
    if (Array.isArray(value)) {
        return JSON.stringify([...value].map((entry) => String(entry)).sort());
    }
    if (typeof value === "string")
        return value.trim().toLowerCase();
    return JSON.stringify(value ?? null);
}
function toDate(value) {
    return value instanceof Date ? value : new Date(value);
}
function monthYear(date) {
    return date.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}
export function carrierListLabel(carriers) {
    const names = carriers
        .map((slug) => {
        const carrier = CARRIER_VALUE_TO_CARRIER[slug];
        return carrier ? CARRIER_DISPLAY_NAMES[carrier] : slug.replace(/[_-]+/g, " ");
    })
        .filter(Boolean);
    if (names.length === 0)
        return "Previous contract";
    if (names.length === 1)
        return names[0];
    if (names.length === 2)
        return `${names[0]} and ${names[1]}`;
    return `${names[0]} +${names.length - 1} more`;
}
function submissionLabel(submission) {
    return `${carrierListLabel(submission.carriers)} contract · ${monthYear(toDate(submission.submittedAt))}`;
}
/**
 * Masks a sensitive value for display. The backend calls this before a plan
 * ever leaves the server, so the wire only carries the tail of each secret.
 */
export function maskSensitiveAutofillValue(fieldId, value) {
    const raw = typeof value === "string" ? value.trim() : String(value ?? "");
    if (!raw)
        return "";
    if (fieldId === "ssn") {
        const digits = raw.replace(/\D/g, "");
        return digits.length >= 4 ? `•••-••-${digits.slice(-4)}` : "•••-••-••••";
    }
    if (fieldId === "dateOfBirth") {
        // Date inputs hold YYYY-MM-DD. Showing only the year is enough for the
        // agent to recognise their own record without re-exposing the full date.
        const year = /^(\d{4})-\d{2}-\d{2}$/.exec(raw)?.[1];
        return year ? `••/••/${year}` : "On file";
    }
    const digits = raw.replace(/\s/g, "");
    return digits.length > 4 ? `${"•".repeat(Math.min(digits.length - 4, 8))}${digits.slice(-4)}` : "••••";
}
function collectObservations(fieldId, submissions) {
    return submissions
        .map((submission) => ({
        value: normalizeValue(submission.formData[fieldId]),
        submission,
        at: toDate(submission.submittedAt),
    }))
        .filter((observation) => !isBlank(observation.value))
        .sort((left, right) => right.at.getTime() - left.at.getTime());
}
function resolveFromHistory(fieldId, policy, observations, now) {
    const [newest, ...older] = observations;
    if (!newest)
        return null;
    const ageDays = (now.getTime() - newest.at.getTime()) / DAY_MS;
    const stale = ageDays > policy.trustDays;
    const newestKey = comparisonKey(newest.value);
    const disagreeing = older.filter((observation) => comparisonKey(observation.value) !== newestKey);
    let reason;
    let confidence;
    if (disagreeing.length > 0) {
        reason = "changed";
        confidence = "medium";
    }
    else if (observations.length > 1) {
        reason = "consistent";
        confidence = "high";
    }
    else {
        reason = "single_source";
        confidence = "high";
    }
    if (stale) {
        reason = "stale";
        // Twice past the window is a guess, not a suggestion.
        confidence = ageDays > policy.trustDays * 2 ? "low" : "medium";
    }
    return {
        fieldId,
        category: policy.category,
        value: newest.value,
        source: "previous_contract",
        sourceLabel: submissionLabel(newest.submission),
        submissionId: newest.submission.id,
        capturedAt: newest.at.toISOString(),
        confidence,
        reason,
        sensitive: Boolean(policy.sensitive),
        needsReview: stale || disagreeing.length > 0,
        ...(disagreeing.length > 0
            ? {
                alternatives: disagreeing.map((observation) => ({
                    value: policy.sensitive
                        ? maskSensitiveAutofillValue(fieldId, observation.value)
                        : observation.value,
                    sourceLabel: submissionLabel(observation.submission),
                    capturedAt: observation.at.toISOString(),
                })),
            }
            : {}),
    };
}
function profileSuggestion(fieldId, value, policy) {
    return {
        fieldId,
        category: policy.category,
        value,
        source: "profile",
        sourceLabel: "Your profile",
        confidence: "high",
        reason: "profile",
        sensitive: false,
        needsReview: false,
    };
}
/**
 * Fields the form itself derives from other answers (see the W9 step in
 * `AgentForm`). Deriving them here too means a returning agent lands on the tax
 * step with it already filled instead of watching it populate as they arrive.
 */
function deriveSuggestions(resolved) {
    const derived = [];
    const read = (fieldId) => {
        const suggestion = resolved.get(fieldId);
        return typeof suggestion?.value === "string" ? suggestion.value.trim() : "";
    };
    const inherit = (fieldId, value, from) => {
        const parents = from
            .map((parentId) => resolved.get(parentId))
            .filter((suggestion) => Boolean(suggestion));
        if (parents.length === 0 || !value)
            return null;
        const weakest = parents.reduce((worst, candidate) => confidenceRank(candidate.confidence) < confidenceRank(worst.confidence) ? candidate : worst);
        const policy = AUTOFILL_FIELD_POLICY[fieldId];
        return {
            fieldId,
            category: policy.category,
            value,
            source: weakest.source,
            sourceLabel: weakest.sourceLabel,
            submissionId: weakest.submissionId,
            capturedAt: weakest.capturedAt,
            confidence: weakest.confidence,
            reason: weakest.reason,
            sensitive: false,
            needsReview: parents.some((parent) => parent.needsReview),
        };
    };
    if (!resolved.has("w9Name")) {
        const name = read("fullName") || [read("firstName"), read("lastName")].filter(Boolean).join(" ");
        const suggestion = inherit("w9Name", name, ["fullName", "firstName", "lastName"]);
        if (suggestion)
            derived.push(suggestion);
    }
    if (!resolved.has("w9Address")) {
        const line1 = read("address");
        const line2 = read("addressLine2");
        const suggestion = inherit("w9Address", line1 ? (line2 ? `${line1}, ${line2}` : line1) : "", ["address"]);
        if (suggestion)
            derived.push(suggestion);
    }
    if (!resolved.has("w9CityStateZip")) {
        const city = read("city");
        const state = read("state");
        const zip = read("zipCode");
        const suggestion = inherit("w9CityStateZip", city && state && zip ? `${city}, ${state} ${zip}` : "", ["city", "state", "zipCode"]);
        if (suggestion)
            derived.push(suggestion);
    }
    return derived;
}
function confidenceRank(confidence) {
    return confidence === "high" ? 3 : confidence === "medium" ? 2 : 1;
}
/**
 * Builds the autofill plan for the agent's next contract application.
 *
 * Precedence is profile → newest agreeing history → newest history. Sensitive
 * values are included in `suggestions` (so the UI can offer them) but excluded
 * from `values`, which is the set safe to apply without asking.
 */
export function buildContractAutofillPlan(input) {
    const now = input.now ?? new Date();
    const submissions = [...(input.submissions ?? [])]
        .filter((submission) => submission.formData && typeof submission.formData === "object")
        .sort((left, right) => toDate(right.submittedAt).getTime() - toDate(left.submittedAt).getTime());
    const resolved = new Map();
    for (const [fieldId, policy] of Object.entries(AUTOFILL_FIELD_POLICY)) {
        if (AUTOFILL_EXCLUDED_FIELD_IDS.has(fieldId))
            continue;
        const suggestion = resolveFromHistory(fieldId, policy, collectObservations(fieldId, submissions), now);
        if (suggestion)
            resolved.set(fieldId, suggestion);
    }
    // Profile identity is canonical: the agent maintains it directly, so it wins
    // over whatever an older application happened to say.
    for (const fieldId of PROFILE_FIELD_IDS) {
        const value = input.profile?.[fieldId];
        if (typeof value !== "string" || !value.trim())
            continue;
        resolved.set(fieldId, profileSuggestion(fieldId, value.trim(), AUTOFILL_FIELD_POLICY[fieldId]));
    }
    // Keep the single Full Name field in step with the first/last pair the rest
    // of the system matches on, preferring whichever of them is canonical.
    const nameParts = ["firstName", "lastName"]
        .map((fieldId) => resolved.get(fieldId))
        .filter((suggestion) => typeof suggestion?.value === "string" && suggestion.value.trim().length > 0);
    const composedName = nameParts.map((part) => String(part.value).trim()).join(" ");
    const existingFullName = resolved.get("fullName");
    if (composedName && (!existingFullName || existingFullName.source === "previous_contract")) {
        const origin = nameParts[0];
        resolved.set("fullName", {
            ...profileSuggestion("fullName", composedName, AUTOFILL_FIELD_POLICY.fullName),
            source: origin.source,
            sourceLabel: origin.sourceLabel,
            submissionId: origin.submissionId,
            capturedAt: origin.capturedAt,
            reason: origin.reason,
            confidence: origin.confidence,
            needsReview: nameParts.some((part) => part.needsReview),
        });
    }
    for (const suggestion of deriveSuggestions(resolved)) {
        resolved.set(suggestion.fieldId, suggestion);
    }
    const suggestions = [...resolved.values()].sort((left, right) => left.fieldId.localeCompare(right.fieldId));
    const values = {};
    for (const suggestion of suggestions) {
        if (suggestion.sensitive)
            continue;
        values[suggestion.fieldId] = suggestion.value;
    }
    return {
        suggestions,
        values,
        reviewFieldIds: suggestions
            .filter((suggestion) => suggestion.needsReview && !suggestion.sensitive)
            .map((suggestion) => suggestion.fieldId),
        sensitiveFieldIds: suggestions
            .filter((suggestion) => suggestion.sensitive)
            .map((suggestion) => suggestion.fieldId),
        sources: submissions.map((submission) => ({
            submissionId: submission.id,
            carriers: submission.carriers,
            carrierLabel: carrierListLabel(submission.carriers),
            submittedAt: toDate(submission.submittedAt).toISOString(),
        })),
        generatedAt: now.toISOString(),
    };
}
/** Replaces sensitive values with masks so a plan can be sent to the browser. */
export function redactAutofillPlan(plan) {
    return {
        ...plan,
        suggestions: plan.suggestions.map((suggestion) => suggestion.sensitive
            ? { ...suggestion, value: maskSensitiveAutofillValue(suggestion.fieldId, suggestion.value) }
            : suggestion),
    };
}
/** One-line summary for the banner above the form. */
export function summarizeAutofillPlan(plan) {
    const filled = Object.keys(plan.values).length;
    if (filled === 0)
        return "Nothing to carry over yet — this is your first contract application.";
    const newest = plan.sources[0];
    const origin = newest
        ? `your ${newest.carrierLabel} contract`
        : "your profile";
    const review = plan.reviewFieldIds.length;
    return review > 0
        ? `Filled ${filled} ${plural(filled, "answer")} from ${origin}. ${review} ${plural(review, "answer")} need${review === 1 ? "s" : ""} a quick check.`
        : `Filled ${filled} ${plural(filled, "answer")} from ${origin}.`;
}
function plural(count, noun) {
    return count === 1 ? noun : `${noun}s`;
}
/**
 * True when a field still holds exactly what autofill put there.
 *
 * This is how the UI knows whether to keep showing provenance: the moment the
 * agent types over a suggestion the badge stops claiming the value came from an
 * old contract. Comparing values rather than tracking edits means a resumed
 * draft gets the same answer as a fresh one.
 */
export function autofillValueMatches(suggestion, value) {
    // Sensitive suggestions travel masked, so a match would be meaningless.
    if (suggestion.sensitive)
        return false;
    if (isBlank(value))
        return false;
    return comparisonKey(value) === comparisonKey(suggestion.value);
}
/** Short explanation rendered under an autofilled field. */
export function describeAutofillSuggestion(suggestion) {
    switch (suggestion.reason) {
        case "profile":
            return "From your profile";
        case "consistent":
            return `Same on every previous contract — latest from ${suggestion.sourceLabel}`;
        case "single_source":
            return `From ${suggestion.sourceLabel}`;
        case "changed":
            return `Changed between contracts — using your most recent answer from ${suggestion.sourceLabel}`;
        case "stale":
            return `From ${suggestion.sourceLabel} — please confirm it is still current`;
        default:
            return `From ${suggestion.sourceLabel}`;
    }
}
